import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { omit } from 'lodash-es';

import {
  ACTORS,
  CUSTOM_FIELD_DATA_TYPE,
  CUSTOM_FIELD_TARGET,
  CustomFieldDefinition,
  CustomFieldDefinitionCreate,
  CustomFieldDefinitionOption,
  PLACE_TYPE,
} from '../../models/main';
import { CustomFieldDefinitionsService } from '../../services/custom-field-definitions.service';
import { FormBase } from '../../shared/form-base';

export interface CustomFieldDefinitionModalData {
  /** Definicion a editar. Sin ella se crea una nueva */
  definition?: CustomFieldDefinition;
  /** Valores con los que se precarga una definicion nueva (p. ej. desde un campo libre) */
  initial?: Partial<CustomFieldDefinitionCreate>;
  /** Fija el `target` de una definicion nueva */
  lockTarget?: boolean;
}

const KEY_PATTERN = /^[a-zA-Z0-9_-]+$/;
const MAX_LENGTH = 100;

/**
 * Crea o edita una definicion del catalogo de custom fields. Se usa desde la gestion del catalogo en los ajustes de la
 * organizacion y desde un campo libre ("guardar como definicion"). Devuelve la definicion guardada al cerrar.
 *
 * `target`, `key` y `dataType` no se pueden cambiar una vez creada: los valores ya guardados dependen de ellos, asi que en
 * edicion se muestran como texto. Por el mismo motivo las opciones de `select` ya guardadas no cambian de clave ni se
 * borran: solo se edita su etiqueta y se archivan o restauran. El back archiva cualquier opcion guardada que falte en el
 * PATCH, por eso se envia siempre la lista completa con su `archived`.
 */
@Component({
  selector: 'app-custom-field-definition-modal',
  templateUrl: './custom-field-definition-modal.component.html',
  styleUrls: ['./custom-field-definition-modal.component.scss'],
})
export class CustomFieldDefinitionModalComponent extends FormBase implements OnInit {

  TARGETS = Object.values(CUSTOM_FIELD_TARGET);
  DATA_TYPES = Object.values(CUSTOM_FIELD_DATA_TYPE);
  ACTOR_SUBTYPES = Object.values(ACTORS);
  PLACE_TYPES = Object.values(PLACE_TYPE);
  DATA_TYPE = CUSTOM_FIELD_DATA_TYPE;
  TARGET = CUSTOM_FIELD_TARGET;

  form!: FormGroup;
  saving = false;
  /** Mientras el usuario no toque la clave, se deriva de la etiqueta */
  private keyEdited = false;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<CustomFieldDefinitionModalComponent, CustomFieldDefinition>,
    private definitionsService: CustomFieldDefinitionsService,
    @Inject(MAT_DIALOG_DATA) public data: CustomFieldDefinitionModalData,
  ) { super(); }

  get isEdit(): boolean {
    return !!this.data.definition;
  }

  get options(): FormArray {
    return this.form.get('options') as FormArray;
  }

  ngOnInit(): void {
    const source: Partial<CustomFieldDefinition> = this.data.definition ?? this.data.initial ?? {};
    this.form = this.fb.group({
      target: [source.target ?? CUSTOM_FIELD_TARGET.Record, Validators.required],
      label: [source.label ?? '', [Validators.required, Validators.maxLength(MAX_LENGTH)]],
      key: [source.key ?? CustomFieldDefinitionModalComponent.toKey(source.label ?? ''), [Validators.required, Validators.maxLength(MAX_LENGTH), Validators.pattern(KEY_PATTERN)]],
      dataType: [source.dataType ?? CUSTOM_FIELD_DATA_TYPE.Text, Validators.required],
      actorSubtypes: [source.actorSubtypes ?? []],
      placeTypes: [source.placeTypes ?? []],
      alwaysShow: [source.alwaysShow ?? false],
      options: this.fb.array((source.options ?? []).map(option => this.optionFormFactory(option, this.isEdit)), CustomFieldDefinitionModalComponent.uniqueOptionKeys),
    });
    this.keyEdited = !!source.key;
    if (this.isEdit) {
      ['target', 'key', 'dataType'].forEach(name => this.form.get(name)?.disable());
    } else if (this.data.lockTarget) {
      this.form.get('target')?.disable();
    }
    if (this.form.get('dataType')?.value === CUSTOM_FIELD_DATA_TYPE.Select && !this.options.length) this.addOption();

    this.form.get('label')?.valueChanges.subscribe(label => {
      if (!this.isEdit && !this.keyEdited) this.form.get('key')?.setValue(CustomFieldDefinitionModalComponent.toKey(label ?? ''));
    });
  }

  onKeyInput(): void {
    this.keyEdited = true;
  }

  selectDataType(): void {
    if (this.form.get('dataType')?.value === CUSTOM_FIELD_DATA_TYPE.Select) {
      if (!this.options.length) this.addOption();
    } else {
      this.options.clear();
    }
  }

  addOption(): void {
    this.addFormElement(this.options, this.optionFormFactory());
  }

  /** Una opcion `saved` ya existe en el back: su clave es fija y no se puede quitar, solo archivar */
  optionFormFactory(option?: Partial<CustomFieldDefinitionOption>, saved = false): FormGroup {
    const group = this.fb.group({
      key: [{ value: option?.key ?? CustomFieldDefinitionModalComponent.toKey(option?.label ?? ''), disabled: saved }, [Validators.required, Validators.maxLength(MAX_LENGTH), Validators.pattern(KEY_PATTERN)]],
      label: [option?.label ?? '', [Validators.required, Validators.maxLength(MAX_LENGTH)]],
      archived: [option?.archived ?? false],
      saved: [saved],
    });
    if (!saved) {
      // Mientras el usuario no toque la clave de la opcion nueva, se deriva de su etiqueta
      let keyEdited = !!option?.key;
      group.get('key')?.valueChanges.subscribe(() => keyEdited = true);
      group.get('label')?.valueChanges.subscribe(label => {
        if (!keyEdited) group.get('key')?.setValue(CustomFieldDefinitionModalComponent.toKey(label ?? ''), { emitEvent: false });
      });
    }
    return group;
  }

  toggleOptionArchived(option: AbstractControl): void {
    const archived = option.get('archived');
    archived?.setValue(!archived.value);
  }

  close(): void {
    this.dialogRef.close();
  }

  save(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid || this.saving) return;
    const value = this.form.getRawValue();
    const isSelect = value.dataType === CUSTOM_FIELD_DATA_TYPE.Select;
    if (isSelect && !value.options.length) return;
    const options: CustomFieldDefinitionOption[] | null = isSelect
      ? value.options.map((option: CustomFieldDefinitionOption) => ({ key: option.key, label: option.label.trim(), archived: option.archived }))
      : null;
    const actorSubtypes = value.target === CUSTOM_FIELD_TARGET.Actor && value.actorSubtypes?.length ? value.actorSubtypes : null;
    const placeTypes = value.target === CUSTOM_FIELD_TARGET.Place && value.placeTypes?.length ? value.placeTypes : null;

    this.saving = true;
    const request = this.isEdit
      ? this.definitionsService.update(this.data.definition!.id, { label: value.label.trim(), actorSubtypes, placeTypes, options, alwaysShow: value.alwaysShow })
      : this.definitionsService.create({
        target: value.target,
        label: value.label.trim(),
        key: value.key,
        dataType: value.dataType,
        actorSubtypes,
        placeTypes,
        options,
        alwaysShow: value.alwaysShow,
        ...(this.data.initial?.order !== undefined ? { order: this.data.initial.order } : {}),
      });
    request.subscribe({
      next: definition => this.dialogRef.close(definition),
      error: () => this.saving = false,
    });
  }

  /** Clave valida para la API a partir de una etiqueta: sin acentos, en minusculas y con guiones */
  static toKey(label: string): string {
    return label.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, MAX_LENGTH);
  }

  /**
   * Las claves de las opciones de una definicion no se pueden repetir. Solo se comprueban las opciones nuevas, que son
   * las unicas con clave editable: una coincidencia entre opciones ya guardadas no se podria corregir desde el modal.
   * El error se marca tambien en la clave de cada opcion nueva repetida.
   */
  static uniqueOptionKeys(control: AbstractControl): ValidationErrors | null {
    const groups = (control as FormArray).controls;
    const keys = groups.map(group => group.get('key')?.value);
    let duplicated = false;
    groups.forEach((group, index) => {
      const key = group.get('key');
      if (!key || group.get('saved')?.value) return;
      const isDuplicate = !!keys[index] && keys.some((other, otherIndex) => otherIndex !== index && other === keys[index]);
      const others = omit(key.errors ?? {}, 'duplicated');
      if (isDuplicate) {
        duplicated = true;
        key.setErrors({ ...others, duplicated: true });
      } else if (key.hasError('duplicated')) {
        key.setErrors(Object.keys(others).length ? others : null);
      }
    });
    return duplicated ? { duplicatedKeys: true } : null;
  }
}
